import React from 'react';
import { getActivityStats, classifyRouteType } from '../../activities/activityTypes';
import GpxTrackView from '../../components/GpxTrackView/GpxTrackView';
import {
  FIELD_KEYS,
  pick,
  isTruthy,
  DetailTopBar,
  DetailHero,
  DetailTitleSheet,
  WeatherPlaceholder,
} from './ActivityDetailPage';

// Ciclismo-specific field keys (on top of the shared ones).
const CICLISMO_KEYS = {
  rampaMax: ['rampaMax', 'maxGrade', 'pendienteMax', 'rampaMaxima'],
  altMax: ['maxAltitude', 'altitudMax', 'cotaMaxima', 'altMax'],
  asfalto: ['asfalto', 'asphalt', 'asphaltPct', 'porcentajeAsfalto'],
  track: ['track', 'trackPct', 'pista', 'porcentajeTrack'],
  fisica: ['dificultadFisica', 'physicalDifficulty', 'fisica'],
  tecnica: ['dificultadTecnica', 'technicalDifficulty', 'tecnica'],
  firme: ['firme', 'surface', 'terreno'],
  circular: ['routeShape', 'recorrido', 'circular', 'tipoRecorrido'],
};

/** BAJA / MEDIA / ALTA label for a 1..5 difficulty level. */
function diffLabel(n) {
  if (n <= 2) return 'BAJA';
  if (n === 3) return 'MEDIA';
  return 'ALTA';
}

/**
 * Ciclismo detail page. Follows the v3 "03 · Ciclismo" design: cota belt, hero,
 * beveled title, surface split (asfalto/track), a data grid and the physical /
 * technical difficulty meters. Any datum absent from the activity is omitted;
 * the weather forecast is a placeholder.
 */
function CiclismoDetail({ activity }) {
  const values = activity.values || {};
  const description = pick(values, FIELD_KEYS.description);
  const place = pick(values, FIELD_KEYS.location);
  const gpx = pick(values, FIELD_KEYS.gpx);
  const circular = pick(values, CICLISMO_KEYS.circular);
  const firme = pick(values, CICLISMO_KEYS.firme);

  const stats = getActivityStats(activity);
  const dist = stats.find((s) => s.unit === 'km');
  const gain = stats.find((s) => s.unit === 'D+');
  const time = stats.find((s) => s.unit === 'horas');
  const routeType = stats.find((s) => s.kind === 'routeType' && s.present);
  const rampaMax = pick(values, CICLISMO_KEYS.rampaMax);
  const altMax = pick(values, CICLISMO_KEYS.altMax);

  const tiles = [
    dist?.present && { key: 'dist', label: 'DISTANCIA', value: dist.value, unit: 'km' },
    gain?.present && { key: 'gain', label: 'DESNIVEL +', value: gain.value, unit: 'm' },
    time?.present && { key: 'time', label: 'TIEMPO', value: time.value, unit: 'h' },
    rampaMax && { key: 'rampa', label: 'RAMPA MÁX.', value: rampaMax, unit: '%' },
    altMax && { key: 'alt', label: 'ALTITUD MÁX.', value: altMax, unit: 'm' },
  ].filter(Boolean);

  const asf = Number(pick(values, CICLISMO_KEYS.asfalto));
  const trk = Number(pick(values, CICLISMO_KEYS.track));
  const hasSurface = Number.isFinite(asf) || Number.isFinite(trk);

  const fisica = Number(pick(values, CICLISMO_KEYS.fisica));
  const tecnica = Number(pick(values, CICLISMO_KEYS.tecnica));
  const routeLabel = routeType ? (classifyRouteType(routeType.value) === 'mtb' ? 'MTB' : 'CARRETERA') : null;

  return (
    <div className="Detail">
      <DetailTopBar
        activity={activity}
        rightCell={gpx && <span className="Detail__barCell Detail__barCell--gpx">GPX</span>}
      />

      <DetailHero activity={activity} />

      <DetailTitleSheet
        eyebrow={[routeLabel, circular && (isTruthy(circular) ? 'CIRCULAR' : String(circular).toUpperCase()), place && String(place).toUpperCase()]
          .filter(Boolean)
          .join(' · ')}
        title={activity.name}
      />

      <div className="Detail__body Detail__body--single">
        <div className="Detail__col">
          {description && (
            <section className="Detail__section">
              <div className="Detail__sectionLabel">DESCRIPCIÓN</div>
              {String(description)
                .split(/\n{2,}/)
                .map((para, i) => (
                  <p key={i} className="Detail__desc">{para}</p>
                ))}
            </section>
          )}

          <GpxTrackView gpxFileId={activity.gpxFileIds?.[0]} />

          {hasSurface && (
            <section className="Detail__section">
              <div className="Detail__sectionLabel">SUPERFICIE</div>
              <div className="Detail__surface">
                {Number.isFinite(asf) && (
                  <div
                    className="Detail__surfaceSeg Detail__surfaceSeg--asphalt"
                    style={{ flex: Number.isFinite(trk) ? asf : 1 }}
                  >
                    {asf} %
                  </div>
                )}
                {Number.isFinite(trk) && (
                  <div
                    className="Detail__surfaceSeg Detail__surfaceSeg--track"
                    style={{ flex: Number.isFinite(asf) ? trk : 1 }}
                  >
                    {trk} % TRACK
                  </div>
                )}
              </div>
              {(circular || firme) && (
                <div className="Detail__surfaceMeta">
                  {circular && <span>TIPO · {isTruthy(circular) ? 'CIRCULAR' : String(circular).toUpperCase()}</span>}
                  {firme && <span>FIRME · {String(firme).toUpperCase()}</span>}
                </div>
              )}
            </section>
          )}

          {tiles.length > 0 && (
            <div className="Detail__stats Detail__stats--wide">
              {tiles.map(({ key, label, value, unit }) => (
                <div key={key} className="Detail__stat">
                  <span className="Detail__statLabel">{label}</span>
                  <span className="Detail__statVal">
                    {value} <em className="Detail__statUnit">{unit}</em>
                  </span>
                </div>
              ))}
            </div>
          )}

          {(Number.isFinite(fisica) || Number.isFinite(tecnica)) && (
            <div className="Detail__diffRow">
              {Number.isFinite(fisica) && (
                <DifficultyMeter label="DIFICULTAD FÍSICA" value={fisica} />
              )}
              {Number.isFinite(tecnica) && (
                <DifficultyMeter label="DIFICULTAD TÉCNICA" value={tecnica} />
              )}
            </div>
          )}

          <WeatherPlaceholder activity={activity} />
        </div>
      </div>

      {gpx && (
        <a className="Detail__gpxFooter" href={gpx} download>
          <span className="Detail__gpxFooterText">
            <span>SIGUIENTE PASO</span>
            <strong>DESCARGAR GPX</strong>
          </span>
          <span className="Detail__gpxFooterIcon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="butt">
              <path d="M12 4 L12 16 M6 11 L12 17 L18 11 M4 20 L20 20" />
            </svg>
          </span>
        </a>
      )}
    </div>
  );
}

/** Cream difficulty card: big "N / 5 · LABEL" plus a 5-segment meter. */
function DifficultyMeter({ label, value }) {
  return (
    <div className="Detail__diffCard flik-cut-lg">
      <span className="Detail__diffLabel">{label}</span>
      <span className="Detail__diffVal">
        {value} <em>/ 5 · {diffLabel(value)}</em>
      </span>
      <div className="Detail__diffScale" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((n) => (
          <span key={n} className={`Detail__diffSeg${n <= value ? ' is-on' : ''}`} />
        ))}
      </div>
    </div>
  );
}

export default CiclismoDetail;
